import React, { useState, useEffect } from "react";
import Navigation from "../components/Navigation";
import Footer from "../components/Footer";
import { Link, useSearchParams } from "react-router-dom";
import { products, filterOptions } from "../data/products";
import { Filter, Grid, List } from "lucide-react";

const Products = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Filter to show ONLY Peakstone products (ComfortSense, SolidSense, NatureSense, Accessories)
  // Exclude Onn Surfaces (Guard, Beat, Pulse) and ProMaster (ProSeries)
  const peakstoneCollections = ['ComfortSense', 'SolidSense', 'NatureSense', 'Accessories'];
  const allowedTypes = ['laminate', 'vinyl', 'hardwood', 'underlay', 'stair_nosings', 't-mouldings', 'reducers', 'accessory'];
  
  const peakstoneProducts = products.filter(p => 
    peakstoneCollections.includes(p.collection) && allowedTypes.includes(p.type)
  );
  
  const [filteredProducts, setFilteredProducts] = useState(peakstoneProducts);
  const [activeFilter, setActiveFilter] = useState("all");
  const [viewMode, setViewMode] = useState("grid");
  const [productsPerPage, setProductsPerPage] = useState(12);

  useEffect(() => {
    const filter = searchParams.get("filter") || "all";
    setActiveFilter(filter);
    
    if (filter === "all") {
      setFilteredProducts(peakstoneProducts);
    } else {
      const filtered = peakstoneProducts.filter(
        (product) => 
          product.type === filter || 
          product.collection === filter
      );
      setFilteredProducts(filtered);
    }
  }, [searchParams]);

  const handleFilterChange = (filterValue) => {
    setActiveFilter(filterValue);
    if (filterValue === "all") {
      setSearchParams({});
    } else {
      setSearchParams({ filter: filterValue });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 luxury-texture">
      <Navigation />
      
      {/* Premium Banner */}
      <div className="bg-gradient-to-r from-luxury-navy via-luxury-charcoal to-luxury-navy py-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0" style={{
            backgroundImage: 'radial-gradient(circle at 2px 2px, rgba(212, 175, 55, 0.4) 1px, transparent 0)',
            backgroundSize: '50px 50px'
          }}></div>
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="w-32 h-1 bg-luxury-gold mx-auto mb-6 luxury-shimmer"></div>
          <h1 className="text-5xl md:text-7xl font-bold text-white mb-6 luxury-heading">
            Exclusive <span className="text-luxury-gold">Collection</span>
          </h1>
          <p className="text-gray-300 text-xl md:text-2xl max-w-3xl mx-auto leading-relaxed">
            Discover our curated selection of premium flooring solutions from world-class manufacturers. 
            Factory direct pricing ensures exceptional value without compromising on luxury.
          </p>
        </div>
      </div>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar Filters */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-md p-6 sticky top-24">
              <div className="flex items-center mb-4">
                <Filter className="w-5 h-5 text-blue-600 mr-2" />
                <h2 className="text-xl font-bold text-gray-800">Filters</h2>
              </div>

              {/* Flooring Types Filter */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">
                  Flooring Types
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleFilterChange("all")}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeFilter === "all"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    All Products
                  </button>
                  <button
                    onClick={() => handleFilterChange("laminate")}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeFilter === "laminate"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Water-Resistant Laminate
                  </button>
                  <button
                    onClick={() => handleFilterChange("vinyl")}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeFilter === "vinyl"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Rigid-Core Vinyl (SPC)
                  </button>
                  <button
                    onClick={() => handleFilterChange("hardwood")}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeFilter === "hardwood"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Engineered Hardwood
                  </button>
                </div>
              </div>

              {/* Accessories Filter */}
              <div className="mb-6">
                <h3 className="font-semibold text-gray-800 mb-3">
                  Accessories
                </h3>
                <div className="space-y-2">
                  <button
                    onClick={() => handleFilterChange("underlay")}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeFilter === "underlay"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Underlayments
                  </button>
                  <button
                    onClick={() => handleFilterChange("stair_nosings")}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeFilter === "stair_nosings"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Stair Nosings
                  </button>
                  <button
                    onClick={() => handleFilterChange("t-mouldings")}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeFilter === "t-mouldings"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    T-Mouldings
                  </button>
                  <button
                    onClick={() => handleFilterChange("reducers")}
                    className={`w-full text-left px-3 py-2 rounded-lg transition-colors ${
                      activeFilter === "reducers"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    Reducers
                  </button>
                </div>
              </div>

              {/* Contact CTA */}
              <div className="mt-6 p-4 bg-blue-50 rounded-lg">
                <p className="text-sm text-gray-700 mb-3">
                  Need help choosing the right flooring?
                </p>
                <Link
                  to="/request-quote"
                  className="block w-full bg-blue-600 text-white text-center py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors"
                >
                  Request Quote
                </Link>
              </div>
            </div>
          </div>

          {/* Products Grid */}
          <div className="lg:col-span-3">
            {/* Toolbar */}
            <div className="bg-white rounded-lg shadow-md p-4 mb-6 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
              <div className="text-gray-700">
                Showing <span className="font-semibold">{filteredProducts.length}</span> products
              </div>
              <div className="flex items-center space-x-4">
                <div className="flex items-center space-x-2">
                  <label className="text-gray-700 text-sm">View:</label>
                  <button
                    onClick={() => setViewMode("grid")}
                    className={`p-2 rounded ${
                      viewMode === "grid"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <Grid className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => setViewMode("list")}
                    className={`p-2 rounded ${
                      viewMode === "list"
                        ? "bg-blue-600 text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    <List className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>

            {/* Products Display */}
            {filteredProducts.length === 0 ? (
              <div className="bg-white rounded-lg shadow-md p-12 text-center">
                <p className="text-gray-500 text-lg">
                  No products found. Please try a different filter.
                </p>
              </div>
            ) : (
              <div
                className={
                  viewMode === "grid"
                    ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
                    : "space-y-4"
                }
              >
                {filteredProducts.slice(0, productsPerPage).map((product) => (
                  <div
                    key={product.id}
                    className={`ultra-luxury-card rounded-sm overflow-hidden group ${
                      viewMode === "list" ? "flex" : ""
                    }`}
                  >
                    <div className="relative overflow-hidden">
                      <div className="absolute inset-0 bg-luxury-gold/0 group-hover:bg-luxury-gold/10 transition-colors duration-500 z-10"></div>
                      <div className="absolute inset-0 border-2 border-luxury-gold opacity-0 group-hover:opacity-100 transition-opacity duration-500 z-10 m-2"></div>
                      <img
                        src={product.image}
                        alt={product.name}
                        className={`object-cover transform group-hover:scale-110 transition-transform duration-700 ${
                          viewMode === "list"
                            ? "w-48 h-full"
                            : "w-full h-56"
                        }`}
                      />
                    </div>
                    <div className="p-6 flex-1">
                      <div className="mb-3 flex items-center justify-between">
                        <span className="text-xs font-semibold text-luxury-gold bg-luxury-gold/10 px-3 py-1 rounded-sm border border-luxury-gold/30 luxury-subheading">
                          {product.collection}
                        </span>
                        {product.wearLayer && (
                          <span className="text-xs text-luxury-slate bg-gray-100 px-2 py-1 rounded-sm">
                            {product.wearLayer}
                          </span>
                        )}
                      </div>
                      <h3 className="text-2xl md:text-3xl font-bold text-luxury-navy mb-2 luxury-heading">
                        {product.name}
                      </h3>
                      <p className="text-gray-600 text-sm mb-4">
                        {product.category}
                      </p>
                      
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Thickness:</span>
                          <span className="font-medium text-gray-800">
                            {product.thickness}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600">Warranty:</span>
                          <span className="font-medium text-gray-800">
                            {product.warranty}
                          </span>
                        </div>
                      </div>

                      <div className="mb-4">
                        <p className="text-xs text-gray-600 mb-2">Key Features:</p>
                        <div className="flex flex-wrap gap-1">
                          {product.features.map((feature, index) => (
                            <span
                              key={index}
                              className="text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded"
                            >
                              {feature}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div className="flex items-center justify-between pt-4 border-t border-luxury-gold/20">
                        <span className="text-luxury-navy font-semibold text-lg">
                          {product.price}
                        </span>
                        <Link
                          to="/request-quote"
                          className="luxury-button text-luxury-navy px-5 py-2 rounded-sm text-sm font-semibold inline-flex items-center group"
                        >
                          <span>Request Quote</span>
                          <svg className="w-4 h-4 ml-1 group-hover:translate-x-1 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
                          </svg>
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Load More */}
            {filteredProducts.length > productsPerPage && (
              <div className="mt-8 text-center">
                <button
                  onClick={() => setProductsPerPage(productsPerPage + 12)}
                  className="bg-white text-blue-600 border-2 border-blue-600 px-8 py-3 rounded-lg font-semibold hover:bg-blue-600 hover:text-white transition-colors"
                >
                  Load More Products
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Products;
